import {
  ApolloLink,
  Observable,
  makeVar,
  useReactiveVar,
} from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';

// in-flight mutation counts keyed by operation name
export const pendingMutationsVar = makeVar({});

// operation names per saving group, add a new group here (e.g. incident) to extend useIsSaving
const savingGroups = {
  // mutations used in hooks/offline-hooks/createInspectionhook.js
  inspection: [
    'CreateAssessmentFromModal',
    'UpdateInspectionNotes',
    'UpdateInspectionOnline',
    'AddInspectionItem',
    'UpdateInspectionItem',
    'DeleteInspectionItemUser',
    'AddInspectionItemUser',
    'PublishInspection',
    'SubmitBundle',
    'DeleteBundle',
    'AddInspectionParticipantFromSidebar',
    'AddCategoryStatus',
  ],
};

const updatePending = (operationName, delta) => {
  const pending = pendingMutationsVar();
  const count = Math.max(0, (pending[operationName] ?? 0) + delta);
  pendingMutationsVar({ ...pending, [operationName]: count });
};

export const mutationTrackingLink = new ApolloLink((operation, forward) => {
  const definition = getMainDefinition(operation.query);
  if (
    definition.kind !== 'OperationDefinition' ||
    definition.operation !== 'mutation'
  ) {
    return forward(operation);
  }

  const { operationName } = operation;
  updatePending(operationName, 1);

  return new Observable((observer) => {
    let finished = false;
    const done = () => {
      if (!finished) {
        finished = true;
        updatePending(operationName, -1);
      }
    };
    const subscription = forward(operation).subscribe({
      next: (value) => observer.next(value),
      error: (error) => {
        done();
        observer.error(error);
      },
      complete: () => {
        done();
        observer.complete();
      },
    });
    return () => {
      done();
      subscription.unsubscribe();
    };
  });
});

const isGroupSaving = (pending, group) =>
  savingGroups[group].some((name) => pending[name] > 0);

export const useIsSaving = () => {
  const pending = useReactiveVar(pendingMutationsVar);
  return {
    isSaving: Object.values(pending).some((count) => count > 0),
    isSavingInspection: isGroupSaving(pending, 'inspection'),
  };
};
